import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type ISystemPreferencesRepository,
  type SystemPreferenceMap,
} from '@webapp/platform/iam/business-logic/interfaces/ISystemPreferencesRepository'
import { SystemPreferenceKeys } from '@webapp/platform/iam/entities/enums/SystemPreferenceKeys'
import {
  type POPDFHiddenCustomField,
  type SystemPreferenceConstructor,
} from '@webapp/platform/iam/entities/SystemPreference'
import { type POPDFHiddenCustomFieldApiModel } from '../../api-models/POPDFHiddenCustomFieldApiModel'
import { type SystemPreferencesAPIModel } from '../../api-models/SystemPreferencesAPIModel'
import {
  mapPOPDFHiddenCustomFieldApiModel,
  mapPOPDFHiddenCustomFieldToApiModel,
} from '../../mappers/mapPOPDFHiddenCustomField'
import { type GetSystemPreferencesResponse } from '../../types/getSystemPreferencesResponse'
import { type UpdateSystemPreferencesResponse } from '../../types/updateSystemPreferencesResponse'

interface Dependency {
  httpClient: IHttpClient
}

const isSystemPreferenceItem = <K extends keyof SystemPreferenceMap>(
  systemPreference: unknown,
  key: K
): systemPreference is SystemPreferenceConstructor<SystemPreferenceMap[K]> => {
  return (
    typeof systemPreference === 'object' &&
    systemPreference !== null &&
    'key' in systemPreference &&
    (systemPreference as { key: unknown }).key === key
  )
}

// The hidden-map preference is the only snake_case preference value — map it
// to the POPDFHiddenCustomField domain shape on reads and back to the wire
// shape on writes so the raw shape never leaks past the repository.
const mapPreferenceValueFromApi = (
  systemPreference: SystemPreferencesAPIModel | undefined
): SystemPreferencesAPIModel | undefined => {
  if (
    systemPreference?.key ===
      SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN &&
    Array.isArray(systemPreference.value)
  ) {
    return {
      ...systemPreference,
      value: (systemPreference.value as POPDFHiddenCustomFieldApiModel[]).map(
        mapPOPDFHiddenCustomFieldApiModel
      ),
    }
  }

  return systemPreference
}

const mapPreferenceValueToApi = <K extends keyof SystemPreferenceMap>(
  key: K,
  value: SystemPreferenceMap[K]['value']
): unknown =>
  key === SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN
    ? (value as POPDFHiddenCustomField[]).map(
        mapPOPDFHiddenCustomFieldToApiModel
      )
    : value

export const makeSystemPreferencesRepository = ({
  httpClient,
}: Dependency): ISystemPreferencesRepository => ({
  getSystemPreference: async <K extends keyof SystemPreferenceMap>(
    key: K
  ): Promise<SystemPreferenceConstructor<SystemPreferenceMap[K]>> => {
    const res = await httpClient.get<GetSystemPreferencesResponse>(
      `/api/v3/kvstore/system-preferences/`
    )

    const systemPreferences = res.data.data
    const systemPreference = mapPreferenceValueFromApi(
      systemPreferences.find((item) => item.key === key)
    )

    if (isSystemPreferenceItem(systemPreference, key)) {
      return systemPreference
    } else {
      throw new Error(`Invalid system preference key: ${key}`)
    }
  },

  updateSystemPreference: async <K extends keyof SystemPreferenceMap>(
    key: K,
    value: SystemPreferenceMap[K]['value']
  ): Promise<SystemPreferenceConstructor<SystemPreferenceMap[K]>> => {
    const res = await httpClient.post<UpdateSystemPreferencesResponse>(
      `/api/v3/kvstore/system-preferences/`,
      {
        key,
        value: mapPreferenceValueToApi(key, value),
      }
    )

    const systemPreference = mapPreferenceValueFromApi(
      res.data.data as unknown as SystemPreferencesAPIModel
    )

    if (isSystemPreferenceItem(systemPreference, key)) {
      return systemPreference
    } else {
      throw new Error(`Invalid system preference key: ${key}`)
    }
  },
})
